import { useState, useRef, useCallback, useEffect } from 'react';
import { X, Upload, Image, MapPin, Globe, Users, Heart, Lock, Loader, Sparkles, Plus, Trash2, ChevronDown, Check } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from '../contexts/LocationContext';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PRIVACY_OPTIONS = [
  { value: 'public',  label: 'Public',   desc: 'Everyone',          icon: Globe,  color: 'text-green-600' },
  { value: 'friends', label: 'Friends',  desc: 'Followers only',    icon: Users,  color: 'text-blue-600'  },
  { value: 'family',  label: 'Family',   desc: 'Family group',      icon: Heart,  color: 'text-purple-600' },
  { value: 'private', label: 'Only Me',  desc: 'Just you',          icon: Lock,   color: 'text-gray-600'  },
];

const VIBES     = ['Comfort','Light & Fresh','Indulgent','Celebrate','Quick Bite','Date Night','Casual','Energizing'];
const CUISINES  = ['Italian','Japanese','Chinese','Indian','Mexican','Thai','French','Mediterranean','Korean','American'];
const OCCASIONS = ['Brunch','Lunch','Dinner','Birthday','Anniversary','Work Lunch','Family Meal','Solo Treat','Date Night'];
const DIETS     = ['Vegetarian','Vegan','Gluten-Free','Halal','Dairy-Free','Keto','Paleo'];

const CreatePostModal = ({ onClose, onPostCreated, isPromo = false, prefill }) => {
  const { user }               = useAuth();
  const { locationString }     = useLocation();
  const { toast }              = useToast();
  const fileRef                = useRef(null);

  const [images, setImages]         = useState([]);   // [{file, preview, id}]
  const [caption, setCaption]       = useState('');
  const [venueName, setVenueName]   = useState('');
  const [location, setLocation]     = useState(locationString?.split(',')[0] || '');
  const [cuisine, setCuisine]       = useState('');
  const [vibe, setVibe]             = useState('');
  const [occasion, setOccasion]     = useState('');
  const [diet, setDiet]             = useState('');
  const [notes, setNotes]           = useState('');
  const [tags, setTags]             = useState('');
  const [privacy, setPrivacy]       = useState('public');
  const [ctaLabel, setCtaLabel]     = useState('Book Now');
  const [ctaUrl, setCtaUrl]         = useState('');
  const [aiLoading, setAiLoading]   = useState(false);
  const [uploading, setUploading]   = useState(false);
  const [error, setError]           = useState('');
  const [showOptional, setShowOptional] = useState(false);
  const [showPrivacy, setShowPrivacy]   = useState(false);

  useEffect(() => {
    if (!prefill) return;
    if (prefill.caption) setCaption(prefill.caption);
    if (prefill.venueName) setVenueName(prefill.venueName);
    if (prefill.location) setLocation(prefill.location);
    if (prefill.cuisine) setCuisine(prefill.cuisine);
    if (prefill.vibe) setVibe(prefill.vibe);
    if (prefill.notes) setNotes(prefill.notes);
    if (prefill.tags) setTags(prefill.tags);
  }, [prefill]);

  const addImages = useCallback((files) => {
    const VIDEO_TYPES = ['video/mp4','video/quicktime','video/avi','video/webm','video/x-matroska','video/x-m4v'];
    const newImgs = Array.from(files)
      .filter(f => f.size <= (VIDEO_TYPES.includes(f.type) ? 100*1024*1024 : 10*1024*1024))
      .slice(0, 10 - images.length)
      .map(f => ({
        file: f,
        preview: URL.createObjectURL(f),
        id: Math.random().toString(36).slice(2),
        isVideo: f.type.startsWith('video/'),
      }));
    setImages(prev => [...prev, ...newImgs].slice(0, 10));
  }, [images.length]);

  const removeImage = (id) => {
    setImages(prev => {
      const img = prev.find(i => i.id === id);
      if (img) URL.revokeObjectURL(img.preview);
      return prev.filter(i => i.id !== id);
    });
  };

  const handleDrop = (e) => { e.preventDefault(); addImages(e.dataTransfer.files); };

  const generateCaption = async () => {
    setAiLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/posts/generate-caption`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ venue_name: venueName, location, cuisine, vibe, occasion, diet, notes, image_count: images.length }),
      });
      if (res.ok) {
        const data = await res.json();
        setCaption(data.caption || '');
        if (data.tags?.length) setTags(data.tags.join(', '));
        toast({ title: 'AI Caption Generated!', description: 'Edit it to make it yours.' });
      }
    } catch { toast({ title: 'AI caption failed', variant: 'destructive' }); }
    finally { setAiLoading(false); }
  };

  const handleSubmit = async () => {
    if (images.length === 0) { setError('Add at least one photo'); return; }
    if (!caption.trim())     { setError('Write a caption'); return; }
    setUploading(true); setError('');
    try {
      const formData = new FormData();
      formData.append('caption', caption);
      formData.append('venue_name', venueName);
      formData.append('location', location);
      formData.append('city', locationString?.split(',')[0] || '');
      formData.append('cuisine', cuisine);
      formData.append('vibe', vibe);
      formData.append('occasion', occasion);
      formData.append('diet', diet);
      formData.append('notes', notes);
      formData.append('tags', tags);
      formData.append('privacy', privacy);
      if (isPromo) { formData.append('cta_label', ctaLabel); formData.append('cta_url', ctaUrl); }
      images.forEach(img => formData.append('images', img.file));

      const endpoint = isPromo ? '/api/posts/promote' : '/api/posts';
      const res = await fetch(`${API_URL}${endpoint}`, { method: 'POST', credentials: 'include', body: formData });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Upload failed'); }
      const post = await res.json();
      onPostCreated?.(post);
      toast({ title: isPromo ? 'Promo post live!' : 'Post shared!', description: 'Your food moment is live.' });
      onClose();
    } catch (err) { setError(err.message); }
    finally { setUploading(false); }
  };

  const selectedPrivacy = PRIVACY_OPTIONS.find(p => p.value === privacy) || PRIVACY_OPTIONS[0];
  const PrivIcon = selectedPrivacy.icon;

  return (
    <div className="fixed inset-0 z-[10000] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white w-full sm:max-w-2xl rounded-t-3xl sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden max-h-[95vh]">

        {/* Header */}
        <div className="bg-gradient-to-r from-[#F4C430] via-[#FF8C42] to-[#FF6B35] px-5 py-4 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-white font-bold text-lg">{isPromo ? '📢 Create Promo Post' : '📸 Share Your Food Moment'}</h2>
            <p className="text-white/75 text-xs">{isPromo ? 'Sponsored · Appears in Feed & Discover' : 'Up to 10 photos · AI caption'}</p>
          </div>
          <button data-testid="create-post-close-btn" onClick={onClose} className="text-white/80 hover:text-white p-1"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* Image upload zone */}
          <div className="p-4 border-b border-gray-100">
            <div
              data-testid="create-post-upload-zone"
              onDrop={handleDrop} onDragOver={(e) => e.preventDefault()}
              onClick={() => images.length < 10 && fileRef.current?.click()}
              className={`border-2 border-dashed rounded-2xl transition-colors ${images.length < 10 ? 'border-[#FF6B35]/40 hover:border-[#FF6B35] cursor-pointer' : 'border-gray-200'}`}
            >
              {images.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 gap-3">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#FFF5F0] to-[#FFF0E5] rounded-2xl flex items-center justify-center">
                    <Image className="w-8 h-8 text-[#FF6B35]" />
                  </div>
                  <div className="text-center">
                    <p className="font-semibold text-gray-700 text-sm">Drop photos here or click to browse</p>
                    <p className="text-xs text-gray-400 mt-1">Up to 10 photos/videos · JPG, PNG, WebP, MP4, MOV · Photos max 10MB, Videos max 100MB</p>
                  </div>
                  <Button variant="outline" size="sm" className="border-[#FF6B35] text-[#FF6B35] rounded-2xl">
                    <Upload className="w-4 h-4 mr-2" />Select Photos
                  </Button>
                </div>
              ) : (
                <div className="p-3">
                  <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
                    {images.map((img, i) => (
                      <div key={img.id} className="relative aspect-square rounded-xl overflow-hidden group">
                        {img.isVideo ? (
                          <video src={img.preview} className="w-full h-full object-cover" muted playsInline />
                        ) : (
                          <img src={img.preview} alt={`photo ${i+1}`} className="w-full h-full object-cover" />
                        )}
                        {i === 0 && <div className="absolute top-1 left-1 bg-[#FF6B35] text-white text-[9px] px-1.5 py-0.5 rounded-full font-bold">{img.isVideo ? '▶ Video' : 'Cover'}</div>}
                        <button onClick={(e) => { e.stopPropagation(); removeImage(img.id); }}
                          className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                    {images.length < 10 && (
                      <div className="aspect-square rounded-xl border-2 border-dashed border-gray-200 flex items-center justify-center cursor-pointer hover:border-[#FF6B35] transition-colors">
                        <Plus className="w-6 h-6 text-gray-400" />
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-2 text-center">{images.length}/10 photos · drag to reorder</p>
                </div>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/*,video/*" multiple style={{ position: 'absolute', width: 0, height: 0, opacity: 0, overflow: 'hidden', pointerEvents: 'none' }} onChange={e => addImages(e.target.files)} />
          </div>

          <div className="p-4 space-y-3">
            {/* Caption + AI button */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-sm font-semibold text-gray-700">Caption *</label>
                <Button size="sm" variant="outline" onClick={generateCaption} disabled={aiLoading}
                  data-testid="create-post-generate-caption-btn"
                  className="h-7 border-[#FF6B35] text-[#FF6B35] text-xs rounded-2xl gap-1.5">
                  {aiLoading ? <Loader className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                  {aiLoading ? 'Generating...' : 'Generate with AI'}
                </Button>
              </div>
              <textarea value={caption} onChange={e => setCaption(e.target.value)} placeholder="What did you discover? 🍽️"
                rows={3} maxLength={500}
                className="w-full text-sm border border-gray-200 rounded-2xl px-3 py-2.5 focus:outline-none focus:border-[#FF6B35] resize-none" />
              <p className="text-xs text-gray-400 text-right">{caption.length}/500</p>
            </div>

            {/* Tags */}
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Tags (comma separated)</label>
              <input value={tags} onChange={e => setTags(e.target.value)} placeholder="ramen, latenight, birthday"
                className="w-full text-sm border border-gray-200 rounded-2xl px-3 py-2 focus:outline-none focus:border-[#FF6B35]" />
            </div>

            {/* Venue + location inline */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">Venue (optional)</label>
                <input value={venueName} onChange={e => setVenueName(e.target.value)} placeholder="Nobu, Dishoom..."
                  className="w-full text-sm border border-gray-200 rounded-2xl px-3 py-2 focus:outline-none focus:border-[#FF6B35]" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">Neighbourhood</label>
                <div className="relative"><MapPin className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-gray-400" />
                  <input value={location} onChange={e => setLocation(e.target.value)} placeholder="Soho, Shoreditch..."
                    className="w-full text-sm border border-gray-200 rounded-2xl pl-7 pr-3 py-2 focus:outline-none focus:border-[#FF6B35]" />
                </div>
              </div>
            </div>

            {/* Expand optional fields */}
            <button onClick={() => setShowOptional(v => !v)}
              className="flex items-center gap-2 text-sm text-[#FF6B35] font-medium hover:underline">
              <ChevronDown className={`w-4 h-4 transition-transform ${showOptional ? 'rotate-180' : ''}`} />
              {showOptional ? 'Hide' : 'Add'} details (Cuisine · Vibe · Occasion · Diet · Notes)
            </button>

            {showOptional && (
              <div className="space-y-3 border border-gray-100 rounded-2xl p-4 bg-gray-50">
                {[
                  ['Cuisine', cuisine, setCuisine, CUISINES],
                  ['Vibe',    vibe,    setVibe,    VIBES],
                  ['Occasion', occasion, setOccasion, OCCASIONS],
                  ['Diet',    diet,    setDiet,    DIETS],
                ].map(([label, val, setter, opts]) => (
                  <div key={label}>
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">{label}</label>
                    <div className="flex flex-wrap gap-1.5">
                      {opts.map(o => (
                        <button key={o} onClick={() => setter(val === o ? '' : o)}
                          className={`text-xs px-2.5 py-1 rounded-full border transition-all ${val === o ? 'bg-[#FF6B35] text-white border-[#FF6B35]' : 'border-gray-200 text-gray-600 hover:border-[#FF6B35]'}`}>
                          {o}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1 block">What did you order? (notes)</label>
                  <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. Truffle pasta + tiramisu"
                    className="w-full text-sm border border-gray-200 rounded-2xl px-3 py-2 focus:outline-none focus:border-[#FF6B35]" />
                </div>
              </div>
            )}

            {/* Promo CTA */}
            {isPromo && (
              <div className="space-y-3 border border-[#FF6B35]/20 rounded-2xl p-4 bg-[#FFF9F0]">
                <p className="text-xs font-semibold text-[#FF6B35]">Call-to-Action Button</p>
                <div className="grid grid-cols-3 gap-2">
                  {['Book Now', 'Order Online', 'View Menu'].map(l => (
                    <button key={l} onClick={() => setCtaLabel(l)}
                      className={`text-xs py-2 rounded-xl border-2 font-medium transition-all ${ctaLabel === l ? 'border-[#FF6B35] bg-[#FFF5F0] text-[#FF6B35]' : 'border-gray-200 text-gray-600'}`}>
                      {l}
                    </button>
                  ))}
                </div>
                <input value={ctaUrl} onChange={e => setCtaUrl(e.target.value)} placeholder="https://yourvenue.com/booking"
                  className="w-full text-sm border border-gray-200 rounded-2xl px-3 py-2 focus:outline-none focus:border-[#FF6B35]" />
              </div>
            )}

            {/* Privacy */}
            {!isPromo && (
              <div className="relative">
                <button onClick={() => setShowPrivacy(v => !v)}
                  className="flex items-center gap-2 border border-gray-200 rounded-2xl px-3 py-2 text-sm hover:border-[#FF6B35] transition-colors w-full">
                  <PrivIcon className={`w-4 h-4 ${selectedPrivacy.color}`} />
                  <span className="font-medium text-gray-800">{selectedPrivacy.label}</span>
                  <span className="text-gray-400 text-xs">— {selectedPrivacy.desc}</span>
                  <ChevronDown className={`w-4 h-4 text-gray-400 ml-auto transition-transform ${showPrivacy ? 'rotate-180' : ''}`} />
                </button>
                {showPrivacy && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-50">
                    {PRIVACY_OPTIONS.map(opt => {
                      const Icon = opt.icon;
                      return (
                        <button key={opt.value} onClick={() => { setPrivacy(opt.value); setShowPrivacy(false); }}
                          className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 border-b border-gray-50 last:border-0 ${privacy === opt.value ? 'bg-[#FFF5F0]' : ''}`}>
                          <Icon className={`w-4 h-4 ${opt.color}`} />
                          <div className="flex-1 text-left">
                            <p className="text-sm font-medium text-gray-900">{opt.label}</p>
                            <p className="text-xs text-gray-500">{opt.desc}</p>
                          </div>
                          {privacy === opt.value && <Check className="w-4 h-4 text-[#FF6B35]" />}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {error && <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-2xl">{error}</p>}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-100 flex gap-3 bg-white flex-shrink-0">
          <Button variant="outline" onClick={onClose} data-testid="create-post-cancel-btn" className="flex-1 rounded-2xl">Cancel</Button>
          <Button onClick={handleSubmit} disabled={uploading || images.length === 0 || !caption.trim()}
            data-testid="create-post-submit-btn"
            className="flex-1 bg-gradient-to-r from-[#F4C430] via-[#FF8C42] to-[#FF6B35] text-white font-semibold rounded-2xl disabled:opacity-50">
            {uploading ? <><Loader className="w-4 h-4 mr-2 animate-spin" />Sharing...</> : isPromo ? '📢 Go Live' : `Share${images.length > 1 ? ` (${images.length} photos)` : ''}`}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CreatePostModal;
